import { UserNotification } from "@/app/@types/user";
import useFormatDate from "@/app/_hooks/useFormatDate";
import { useNotificationsContext } from "@/app/_providers/NotificationsProvider/NotificationsProvider";
import {
  Anchor,
  Box,
  Divider,
  Flex,
  Group,
  Paper,
  Skeleton,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { IconEye, IconEyeCheck, IconEyeClosed } from "@tabler/icons-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Timestamp } from "firebase/firestore";

interface UserNoteProps {
  notification: UserNotification;
  closeNotification: () => void;
}

const UserNote = ({ notification, closeNotification }: UserNoteProps) => {
  const router = useRouter();
  const theme = useMantineTheme();
  const formatDate = useFormatDate();
  const { markAsRead } = useNotificationsContext();
  const [isHovered, setIsHovered] = useState(false);
  const [noteState, setNoteState] = useState(notification);
  const { id, title, body, link, linkText, read, readAt, createdAt, isAdmin } =
    noteState;
  const adminHighlight = isAdmin ?? false;

  useEffect(() => {
    setNoteState(notification);
  }, [notification]);

  const handleMarkAsRead = () => {
    if (!read) {
      markAsRead(id);
      setNoteState((prev) => ({
        ...prev,
        read: true,
        readAt: Timestamp.now(),
      }));
    }
  };

  return (
    <Flex pos="relative" className="card">
      <Box
        pos="absolute"
        right={8}
        top={8}
        onMouseOver={() => {
          setIsHovered(true);
        }}
        onMouseOut={() => {
          setIsHovered(false);
        }}
        style={{ cursor: read ? "default" : "pointer" }}
      >
        {read ? (
          <IconEyeCheck
            cursor="pointer"
            size={16}
            color={theme.colors.green[7]}
            stroke={2}
            style={{ cursor: "default" }}
          />
        ) : isHovered ? (
          <Group gap={5} onClick={handleMarkAsRead}>
            <Text size="xs" c={theme.colors.green[7]}>
              Mark as read
            </Text>
            <IconEye
              cursor="pointer"
              size={16}
              color={theme.colors.green[7]}
              stroke={2}
            />
          </Group>
        ) : (
          <IconEyeClosed cursor="pointer" size={16} stroke={2} />
        )}
      </Box>
      <Paper
        w="100%"
        p="sm"
        withBorder
        shadow={isHovered && !read ? "xs" : "none"}
        bg={adminHighlight ? theme.colors.blue[0] : undefined}
        style={
          adminHighlight
            ? { borderColor: theme.colors.blue[3] }
            : undefined
        }
      >
        <Text fw={500}>{title}</Text>
        <Text size="xs">{formatDate(createdAt)}</Text>
        <Text size="sm">{body}</Text>
        {link ? (
          <Anchor
            size="sm"
            onClick={(e) => {
              e.stopPropagation(); // Prevent parent onClick from firing
              closeNotification();
              handleMarkAsRead();
              router.push(link);
            }}
          >
            {linkText}
          </Anchor>
        ) : null}
      </Paper>
    </Flex>
  );
};

export default UserNote;
